import { useState, useEffect, useCallback } from 'react';
import { tablesService } from '../lib/database';
import { getDataSource } from '../lib/config';
import { initialTables } from '../data/tables';
import type { Table } from '../types';

interface UseTablesReturn {
  tables: Table[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (table: Omit<Table, 'id'>) => Promise<Table>;
  update: (id: number, updates: Partial<Table>) => Promise<Table>;
  remove: (id: number) => Promise<void>;
}

export const useTables = (enabled = true): UseTablesReturn => {
  const dataSourceOnMount = getDataSource();
  const [tables, setTables] = useState<Table[]>(
    dataSourceOnMount === 'mock' ? initialTables : [],
  );
  const [loading, setLoading] = useState(dataSourceOnMount !== 'mock');
  const [error, setError] = useState<string | null>(null);

  const fetchTables = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setTables(initialTables);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await tablesService.getAll();
      setTables(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tables';
      setError(errorMessage);
      console.error('Error fetching tables:', err);
      // Do NOT reset to initial tables on error - keep existing layout so positions are not lost
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      void fetchTables();
      return;
    }

    if (dataSource === 'firebase') {
      setLoading(true);
      setError(null);
      const unsubscribe = tablesService.subscribe((updatedTables) => {
        setTables(updatedTables);
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, [fetchTables, enabled]);

  const create = useCallback(async (table: Omit<Table, 'id'>): Promise<Table> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newTable: Table = {
        ...table,
        id: Math.max(...tables.map(t => t.id), 0) + 1,
      };
      setTables(prev => [...prev, newTable]);
      return newTable;
    }

    setError(null);
    try {
      const newTable = await tablesService.create(table);
      setTables(prev => [...prev, newTable]);
      return newTable;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create table';
      setError(errorMessage);
      throw err;
    }
  }, [tables]);

  const update = useCallback(async (id: number, updates: Partial<Table>): Promise<Table> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const updatedTable = tables.find(t => t.id === id);
      if (!updatedTable) throw new Error('Table not found');
      const newTable = { ...updatedTable, ...updates };
      setTables(prev => prev.map(t => t.id === id ? newTable : t));
      return newTable;
    }

    setError(null);
    try {
      const existing = tables.find((t) => t.id === id);
      const updatedTable = await tablesService.update(id, updates, existing?.firestoreId);
      setTables(prev => prev.map(t => t.id === id ? updatedTable : t));
      return updatedTable;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update table';
      setError(errorMessage);
      throw err;
    }
  }, [tables]);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setTables(prev => prev.filter(t => t.id !== id));
      return;
    }

    setError(null);
    try {
      const existing = tables.find((t) => t.id === id);
      await tablesService.delete(id, existing?.firestoreId);
      setTables(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete table';
      setError(errorMessage);
      throw err;
    }
  }, [tables]);

  return {
    tables,
    loading,
    error,
    refresh: fetchTables,
    create,
    update,
    remove,
  };
};

